import { ChangeDetectionStrategy, OnDestroy, OnInit, Component, ViewEncapsulation, ViewChild, Input, Renderer2, ElementRef, forwardRef, Injector } from '@angular/core';
import { Router, RouterOutlet } from '@angular/router';
import { CdkScrollable } from '@angular/cdk/scrolling';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatDrawer, MatSidenavModule } from '@angular/material/sidenav';
import { RouterLink } from '@angular/router';
import { FuseMediaWatcherService } from '@fuse/services/media-watcher';
import { FileManagerSidebarComponent } from 'app/modules/admin/apps/file-manager/sidebar/file-manager-sidebar.component';
import { Subject, takeUntil } from 'rxjs';
import { FuseAlertComponent, FuseAlertService } from '@fuse/components/alert';
import { FuseHighlightComponent } from '@fuse/components/highlight';
import { CreateFolderComponent } from './modal/create-folder/create-folder.component';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { RenameItemComponent } from './modal/rename-item/rename-item.component';
import { IAlert, Item } from './file-manager.types';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { FuseConfirmationService } from '@fuse/services/confirmation';
import { FileManagerService } from './file-manager.service';
import { EditItemDescriptionComponent } from './modal/edit-item-description/edit-item-description.component';
import { EditItemNoteComponent } from './modal/edit-item-note/edit-item-note.component';
import { Asset } from 'app/models/document';
import { Util } from 'app/models/util';
import { UploadModalComponent } from './modal/upload-modal/upload-modal.component';
import { FileUploadModule } from 'ng2-file-upload';
import { NgIf } from '@angular/common';
import { AlertMessageService } from 'app/services/alert.message.service';
import { debounceTime } from "rxjs/operators";
import { DocumentService } from 'app/services/document.service';
import { EventService } from 'app/services/event.service';
import { HttpClientModule } from '@angular/common/http';
import { SharedPopoupComponent } from 'app/shared-component/shared-popoup/shared-popoup.component';
import { ToastrService } from 'ngx-toastr';
import { MoveComponent } from './modal/move/move.component';
import { AuthService } from 'app/services/auth.service';
import { CopilotAssetDetails } from 'app/models/copilot.model';
import { LpmLabelPipe } from '@fuse/pipes/lpm-label.pipe';

@Component({
    selector: 'file-manager',
    templateUrl: './file-manager.component.html',
    styleUrls: ['./file-manager.component.scss'],
    encapsulation: ViewEncapsulation.None,
    // changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
    imports: [
        RouterOutlet,
        MatSidenavModule,
        FileManagerSidebarComponent,
        MatIconModule,
        RouterLink,
        MatButtonModule,
        CdkScrollable,
        FuseAlertComponent,
        CreateFolderComponent,
        UploadModalComponent,
        RenameItemComponent,
        FileUploadModule,
        NgIf,
        HttpClientModule,
        forwardRef(() => LpmLabelPipe),
    ],
})

export class FileManagerComponent implements OnInit, OnDestroy {
    @Input() defaultView: string = null;
    @ViewChild('fileManagerDrawer') fileManagerDrawer: MatDrawer;
    @ViewChild('fileManagerContainer', { static: true }) fileManagerContainer!: ElementRef;

    //@ViewChild('customUploader') customUploader: UploaderComponent;
    drawerMode: 'over' | 'side' = 'side';
    drawerOpened: boolean = true;
    private _unsubscribeAll: Subject<any> = new Subject<any>();

    trashForm: UntypedFormGroup;
    trashMultiFiles: UntypedFormGroup;
    deleteForm: UntypedFormGroup;
    deleteMultiForm: UntypedFormGroup;
    deleteAllForm: UntypedFormGroup;
    downloadForm: UntypedFormGroup;
    singledownloadForm: UntypedFormGroup;
    restoreForm: UntypedFormGroup;
    editItemDescriptionForm: UntypedFormGroup;
    isLoading: boolean = true;
    private _success = new Subject<IAlert>();
    alert: IAlert = null;

    /**
     * Constructor
     */
    constructor(
        private _matDialog: MatDialog,
        private _fuseMediaWatcherService: FuseMediaWatcherService,
        private _formBuilder: UntypedFormBuilder,
        private _fuseAlertService: FuseAlertService,
        private _fileManagerService: FileManagerService,
        private _fuseConfirmationService: FuseConfirmationService,
        private _alertService: AlertMessageService,
        private docService: DocumentService,
        public util: Util,
        private eventSer: EventService,
        private toastr : ToastrService,
        private _router: Router,
        private _renderer: Renderer2,
        private authService: AuthService,
        private _injector: Injector,
    ) {
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Lifecycle hooks
    // -----------------------------------------------------------------------------------------------------

    /**
     * On init
     */
    ngOnInit(): void {
        // Subscribe to media changes
        this._fuseMediaWatcherService.onMediaChange$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe(({ matchingAliases }) => {
                // Set the drawerMode and drawerOpened
                if (matchingAliases.includes('lg')) {
                    this.drawerMode = 'side';
                    this.drawerOpened = true;
                }
                else {
                    this.drawerMode = 'over';
                    this.drawerOpened = false;
                }
            });

        this.trashForm = this._formBuilder.group(
           this.util.trashForm
        );
        this.trashMultiFiles = this._formBuilder.group(
           this.util.trashMultiFiles
        );
        this.deleteForm = this._formBuilder.group(
           this.util.deleteForm
        );

        this.deleteMultiForm = this._formBuilder.group(
            this.util.deleteMultiForm
         );

        this.deleteAllForm = this._formBuilder.group(
            this.util.deleteAllForm
        );
        this.downloadForm = this._formBuilder.group(
           this.util.downloadForm
        );

        this.singledownloadForm = this._formBuilder.group(
            this.util.singledownloadForm
         );
        this.restoreForm = this._formBuilder.group(
           this.util.restoreForm
        );
        
        // Listen for Endpoint Errors
        // In web-component mode, LayoutWebComponent owns this subscription instead,
        // since it is the single stable root for the widget's lifetime.
        if (!this.util.getIsWebApp()) {
            this._alertService.getService().subscribe((value) => {
                if (value.selfClose) this._success.next(value);
                else this.alert = value;
                if (this.alert) this.errorNotify(this.alert);
            });

            this._success.subscribe((alert) => (this.alert = alert));
            if (this.alert) this.errorNotify(this.alert);
            this._success.pipe(debounceTime(8000)).subscribe(() => (this.alert = null));
        }

        this.defaultView = this._router.parseUrl(this._router.url).queryParams['defaultView'];
        if (this.defaultView) {
            this._renderer.addClass(this.fileManagerContainer.nativeElement, 'dms-wc');
        }
       
    }

    /**
     * Lifecycle hook that is called when the component is destroyed.
     * It is used to perform any necessary cleanup tasks, such as unsubscribing from observables.
     */
    ngOnDestroy(): void {
        // Unsubscribe from all subscriptions
        this._unsubscribeAll.next(null);
        this._unsubscribeAll.complete();
    }
    
    // -----------------------------------------------------------------------------------------------------
    // @ Private methods
    // -----------------------------------------------------------------------------------------------------

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Opens the upload modal dialog.
     */
    uploadFile() {
        const dialogRef = this._matDialog.open(UploadModalComponent);
    }

    /**
     * Opens a dialog to create a new folder.
     * 
     * @param routeFolderId - The ID of the parent folder in the route.
     */
    createFolderDialog(routeFolderId?: string): void {
        const dialogConfig: MatDialogConfig = {
            data: {
                routeFolderId
            }
        };
        const dialogRef = this._matDialog.open(CreateFolderComponent, dialogConfig);
    }

    /**
     * Opens a dialog to rename the specified item.
     * @param item The item to be renamed.
     */
    public renameItemDialog(item: Asset) {
        // Open the dialog
        const dialogRef = this._matDialog.open(
            RenameItemComponent,
            {
                data: {...item} //pass the data object to the modal component
            }
        );

        dialogRef.afterClosed()
            .subscribe((result) => {
                console.log('Rename dialog was closed!');
            });
    }

    /**
     * Opens a dialog for deleting a file.
     * 
     * @param id - The ID of the file to be deleted.
     */
    public openDialog_delete(id): void {
        //Check permissions

        // Open the dialog and save the reference of it
        this.deleteForm = this._formBuilder.group(
            this.util.deleteForm
        );
        const dialogRef = this._fuseConfirmationService.open(this.deleteForm.value);

        // Subscribe to afterClosed from the dialog reference
        dialogRef.afterClosed().subscribe((result) => {
            console.log(result);

            if (result === 'confirmed') {
                this._fileManagerService.deleteItem(id);
            }
        });
    }

    /**
     * Opens a dialog for deleting multiple items.
     * 
     * @param items - An array of items to be deleted.
     */
    public openDialog_delete_multi(ids): void {
        //Check permissions

        // Open the dialog and save the reference of it
        this.deleteMultiForm = this._formBuilder.group(
            this.util.deleteMultiForm
        );
        const dialogRef = this._fuseConfirmationService.open(this.deleteMultiForm.value);

        // Subscribe to afterClosed from the dialog reference
        dialogRef.afterClosed().subscribe((result) => {
            console.log(result);

            if (result === 'confirmed') {
                this._fileManagerService.deleteItem_multi(ids);
            }
        });
    }
    
    /**
     * Update the description of an item
     * @param item The item to update
     */
    editItemDescription(item: any): void {
        // Open the dialog
        const dialogRef = this._matDialog.open(EditItemDescriptionComponent);

        dialogRef.afterClosed()
            .subscribe((result) => {
                console.log('Compose dialog was closed!');
            });
    }

    /**
    * Add/edit a note to an item
    * @param item The item to update
    */
    editItemNote(item: any, docId): void {
        const dialogConfig: MatDialogConfig = {
            width : '800px',
            data: {
              docId : docId,
              note : item,
            }}

        const dialogRef = this._matDialog.open(EditItemNoteComponent,dialogConfig);
        dialogRef.afterClosed()
            .subscribe((result) => {
                console.log('Compose dialog was closed!');
            });
    }

    /**
     * Stops the propagation of the given event.
     * 
     * @param event - The event to stop propagation for.
     */
    public stopPropagation(event: Event): void {
        event.stopPropagation();
    }

    public errorNotify(alert: IAlert) {
        const id = Date.now().toString();
        this.toastr.error(alert.message, "Error", { timeOut: 8000 });
        //TODO : catch the ID of the alert and emit
    }

    /**
     * Opens a dialog to share the specified item.
     * 
     * @param item - The item to be shared.
     */
    shared(item): void {
        const dialogRef = this._matDialog.open(SharedPopoupComponent,
            {
                maxWidth: '800px',
                injector: this._injector,
                data :item
            },
        );
     }

    /**
     * Opens a dialog to move an item.
     * @param item The item to be moved.
     */
    public moveItemDialog(item: Asset) {
        // Open the dialog
        const dialogRef = this._matDialog.open(
            MoveComponent,
            {
                data: item //pass the data object to the modal component
            }
        );

        dialogRef.afterClosed()
            .subscribe((result) => {
                console.log('Rename dialog was closed!');
            });
    }

    /**
     * Opens a dialog to move multiple items.
     * @param item The items to be moved.
     */
    public moveItemDialogMuti(items: Asset[]) {
        // Open the dialog
        const dialogRef = this._matDialog.open(
            MoveComponent,
            {
                data: items //pass the data object to the modal component
            }
        );

        dialogRef.afterClosed()
            .subscribe((result) => {
                console.log('Move dialog was closed!');
            });
    }

}